import { useCallback, useEffect, useRef, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { CheckCircle2, XCircle, AlertCircle, UserRound } from "lucide-react"
import { getAccessLogs, getDisplayFeed } from "../../services/accessService"
import { memberPhotoUrl } from "../../services/memberService"
import { fr } from "../../locales/fr"
import { useAuth } from "../../context/AuthContext"

const t = fr.entranceDisplay

const POLL_MS = 3500
const SHOW_MS = 9000

/** Arrière-plan attente (salle de sport) — remplaçable par REACT_APP_ENTRANCE_IDLE_BG */
const DEFAULT_IDLE_FITNESS_BG =
  process.env.REACT_APP_ENTRANCE_IDLE_BG ||
  "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=1920&q=55"

function denialMessage(event, labels) {
  const key = event?.reason
  if (key && labels.reasonByKey[key]) {
    return labels.reasonByKey[key]
  }
  const st = String(event?.status || "").toLowerCase()
  if (st === "expired") return labels.detail.expired
  return labels.detail.denied
}

/**
 * Écran TV à l’entrée : affiche le dernier passage (autorisé / refusé)
 * en s’appuyant sur les journaux d’accès (même source que le terminal QR / visage).
 */
export default function EntranceDisplay() {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const displayToken = searchParams.get("token")?.trim() || null

  const [gymNameFromFeed, setGymNameFromFeed] = useState(null)
  const gymName = gymNameFromFeed || user?.gym?.name || t.defaultGymName

  const [mode, setMode] = useState("idle")
  const [event, setEvent] = useState(null)

  /** Dernier id de journal déjà pris en compte (pour détecter seulement les *nouveaux* passages). */
  const lastSeenIdRef = useRef(0)
  /** True après la première réponse API qui contient au moins une ligne (calage sans afficher l’historique). */
  const baselineDoneRef = useRef(false)

  const applyLatest = useCallback((logs) => {
    if (!Array.isArray(logs)) return
    if (logs.length === 0) return

    const latest = logs[0]
    const id = Number(latest.id)
    if (!Number.isFinite(id)) return

    if (!baselineDoneRef.current) {
      baselineDoneRef.current = true
      lastSeenIdRef.current = id
      return
    }

    if (id > lastSeenIdRef.current) {
      lastSeenIdRef.current = id
      setEvent(latest)
      const st = String(latest.status || "").toLowerCase()
      setMode(st === "granted" ? "granted" : "denied")
      window.setTimeout(() => {
        setMode("idle")
        setEvent(null)
      }, SHOW_MS)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const tick = async () => {
      try {
        if (displayToken) {
          const raw = await getDisplayFeed(displayToken)
          const logs = Array.isArray(raw) ? raw : raw.entries
          const name = Array.isArray(raw) ? null : raw.gymName
          if (!cancelled && name) setGymNameFromFeed(name)
          if (!cancelled && Array.isArray(logs)) applyLatest(logs)
        } else {
          const logs = await getAccessLogs()
          if (!cancelled) applyLatest(logs)
        }
      } catch {
        /* réseau : on garde l’écran d’attente */
      }
    }
    void tick()
    const id = window.setInterval(() => void tick(), POLL_MS)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [applyLatest, displayToken])

  const photoSrc = event?.photo ? memberPhotoUrl(event.photo) : null
  const statusKey = String(event?.status || "").toLowerCase()
  const denialText = event && mode === "denied" ? denialMessage(event, t) : ""
  const venueWelcomeLine = t.grantedVenue(gymName)

  return (
    <div className="fixed inset-0 z-0 flex min-h-screen flex-col bg-zinc-950 text-white">
      <Link
        to="/"
        className="absolute left-4 top-4 z-[20] rounded-md border border-white/10 bg-black/30 px-3 py-1.5 text-xs text-zinc-400 backdrop-blur hover:text-white"
      >
        {t.backToApp}
      </Link>
      {mode === "idle" && (
        <div className="relative flex flex-1 flex-col overflow-hidden">
          <div
            className="pointer-events-none absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${DEFAULT_IDLE_FITNESS_BG})` }}
            aria-hidden
          />
          <div className="pointer-events-none absolute inset-0 bg-zinc-950/80" aria-hidden />
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/50 via-zinc-950/70 to-black/85"
            aria-hidden
          />
          <div className="pointer-events-none absolute inset-0" aria-hidden>
            <div
              className="animate-entrance-orb absolute -left-24 top-[18%] h-[min(55vw,28rem)] w-[min(55vw,28rem)] rounded-full bg-primary/20 blur-[100px] md:-left-16"
            />
            <div
              className="animate-entrance-orb-delayed absolute -right-20 bottom-[12%] h-[min(48vw,22rem)] w-[min(48vw,22rem)] rounded-full bg-violet-600/20 blur-[90px] md:-right-12"
            />
            <div
              className="animate-entrance-orb absolute left-1/2 top-1/2 h-[min(90vw,40rem)] w-[min(90vw,40rem)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-zinc-800/40 blur-[120px]"
            />
          </div>
          <div className="relative z-[1] flex flex-1 flex-col items-center justify-center px-6 text-center sm:px-10">
            <h1 className="animate-entrance-gym-hero max-w-[95vw] text-balance">
              <span
                className="animate-entrance-gradient-flow block bg-gradient-to-r from-white via-zinc-100 to-primary bg-clip-text text-4xl font-semibold tracking-tight text-transparent sm:text-6xl md:text-7xl lg:text-8xl"
                style={{ WebkitBackgroundClip: "text", backgroundClip: "text" }}
              >
                {gymName}
              </span>
            </h1>
            <p className="animate-entrance-hint mt-10 max-w-lg text-lg text-zinc-400 sm:mt-14 sm:text-2xl md:text-3xl">
              {t.idleHint}
            </p>
          </div>
        </div>
      )}

      {mode === "granted" && event && (
        <div className="relative flex flex-1 flex-col items-center justify-center bg-gradient-to-br from-emerald-950 via-emerald-900 to-black px-4 py-8 sm:px-8">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(16,185,129,0.12),transparent_50%)]" />
          <div className="animate-entrance-result-in relative z-[1] flex w-full max-w-3xl flex-col items-center rounded-[2rem] border border-emerald-400/35 bg-emerald-950/75 p-8 shadow-2xl shadow-emerald-950/50 ring-1 ring-emerald-400/20 backdrop-blur-md sm:rounded-[2.5rem] sm:p-12 md:max-w-4xl">
            {venueWelcomeLine ? (
              <p className="mb-6 text-center text-sm font-medium uppercase tracking-[0.2em] text-emerald-300/90 sm:text-base">
                {venueWelcomeLine}
              </p>
            ) : null}
            <div className="mb-6 flex flex-col items-center">
              {photoSrc ? (
                <img
                  src={photoSrc}
                  alt=""
                  className="h-40 w-40 rounded-full border-4 border-emerald-400/70 object-cover shadow-2xl ring-4 ring-emerald-500/25 sm:h-48 sm:w-48 md:h-56 md:w-56"
                />
              ) : (
                <div
                  className="flex h-40 w-40 flex-col items-center justify-center rounded-full border-4 border-dashed border-emerald-400/50 bg-emerald-900/50 sm:h-48 sm:w-48 md:h-56 md:w-56"
                  title={t.photoPlaceholder}
                >
                  <UserRound className="h-20 w-20 text-emerald-200/80 sm:h-24 sm:w-24" strokeWidth={1.25} aria-hidden />
                </div>
              )}
            </div>
            <CheckCircle2 className="mb-5 h-16 w-16 text-emerald-300 sm:h-20 sm:w-20" strokeWidth={1.25} aria-hidden />
            <p className="text-center text-3xl font-bold tracking-tight text-white sm:text-4xl md:text-5xl lg:text-6xl">
              {event.member || t.fallbackName}
            </p>
            <p className="mt-4 text-center text-xl font-semibold text-emerald-100 sm:text-2xl md:text-3xl">{t.grantedLine}</p>
          </div>
        </div>
      )}

      {mode === "denied" && event && (
        <div className="relative flex flex-1 flex-col items-center justify-center bg-gradient-to-br from-red-950 via-red-950 to-black px-4 py-8 sm:px-8">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(248,113,113,0.12),transparent_50%)]" />
          <div className="animate-entrance-result-in relative z-[1] flex w-full max-w-3xl flex-col items-center rounded-[2rem] border border-red-400/35 bg-red-950/80 p-8 shadow-2xl shadow-red-950/50 ring-1 ring-red-400/20 backdrop-blur-md sm:rounded-[2.5rem] sm:p-12 md:max-w-4xl">
            {photoSrc ? (
              <img
                src={photoSrc}
                alt=""
                className="mb-5 h-28 w-28 rounded-full border-2 border-red-400/55 object-cover shadow-lg sm:h-36 sm:w-36"
              />
            ) : event.member ? (
              <div
                className="mb-5 flex h-28 w-28 items-center justify-center rounded-full border-2 border-dashed border-red-400/45 bg-red-900/40 sm:h-36 sm:w-36"
                aria-hidden
              >
                <UserRound className="h-14 w-14 text-red-200/70 sm:h-20 sm:w-20" strokeWidth={1.25} />
              </div>
            ) : null}
            {statusKey === "expired" ? (
              <AlertCircle className="mb-8 h-20 w-20 text-amber-300 sm:h-24 sm:w-24" strokeWidth={1.25} aria-hidden />
            ) : (
              <XCircle className="mb-8 h-20 w-20 text-red-300 sm:h-24 sm:w-24" strokeWidth={1.25} aria-hidden />
            )}
            <p className="text-center text-2xl font-bold text-white sm:text-3xl md:text-4xl">{t.deniedLine}</p>
            <p className="mt-5 max-w-3xl text-center text-lg leading-relaxed text-red-50 sm:text-xl md:text-2xl lg:text-3xl">
              {denialText}
            </p>
            {event.member ? (
              <p className="mt-8 text-center text-xl font-semibold text-red-100 sm:text-2xl md:text-3xl">{event.member}</p>
            ) : null}
          </div>
        </div>
      )}
    </div>
  )
}
